// CandidatsLibresSection.jsx
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/Label';
import { 
  UserPlus,
  Search, 
  Download, 
  Upload,
  FileText, 
  CheckCircle,
  XCircle,
  Loader2,
  Trash2,
  Eye,
  Edit,
  CreditCard,
  Calendar,
  User,
  Phone,
  Mail,
  MapPin,
  School
} from 'lucide-react';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "./ui/dialog";

import { useExamensContext } from '../contexts/ExamensContext';
import { api } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import EtatVide from './ui/EtatVide';
import Pagination from './ui/Pagination';
import ChampSelect from './ui/ChampSelect';
import PorteurNotification from './ui/PorteurNotification';
import ConfirmDialog from './ConfirmDialog';
import { useNotification } from '../hooks/useNotification';
import { formaterFCFA } from '../utils/montants';
import { telechargerPdf } from '../utils/telechargerPdf';

// Les boutons d'un meme pied de modal partagent taille ET largeur.
const BOUTON_PIED_CLASSES = 'h-9 min-w-[168px] justify-center gap-2';

const EXAMENS_FILTRE = [
  { valeur: 'all', libelle: 'Tous les examens' },
  { valeur: 'CEPE', libelle: 'CEPE' },
  { valeur: 'BEPC', libelle: 'BEPC' },
  { valeur: 'BAC', libelle: 'BACCALAURÉAT' }
];

const EXAMENS_CHOIX = EXAMENS_FILTRE.filter((e) => e.valeur !== 'all');

const STATUTS_DOSSIER = [
  { valeur: 'all', libelle: 'Tous les statuts' },
  { valeur: 'complete', libelle: 'Dossiers complets' },
  { valeur: 'pending', libelle: 'Dossiers en attente' },
  { valeur: 'incomplete', libelle: 'Dossiers incomplets' }
];

const SEXES = [
  { valeur: 'M', libelle: 'Masculin' },
  { valeur: 'F', libelle: 'Féminin' }
];

const MODES_PAIEMENT = [
  { valeur: 'especes', libelle: 'Espèces' },
  { valeur: 'cheque', libelle: 'Chèque' },
  { valeur: 'virement', libelle: 'Virement bancaire' },
  { valeur: 'mobile_money', libelle: 'Mobile Money' },
  { valeur: 'carte_bancaire', libelle: 'Carte bancaire' }
];


// ⚠️ LE CHEMIN D'UN DOCUMENT S'OUVRE TEL QUE LA BASE LE STOCKE, SANS PREFIXE.
//
// `chemin_fichier` vaut deja « /uploads/examens/<fichier> » : c'est
// `ExamenController.js` qui l'ecrit sous cette forme. Le prefixe pose ici,
// `process.env.REACT_APP_API_URL || 'http://localhost:5000'`, n'a JAMAIS
// designe quoi que ce soit de valable. La variable n'est posee nulle part —
// aucun `.env` dans le depot, et `docker-compose.yml` ne passe pas l'argument
// de compilation au service web — donc le repli s'appliquait TOUJOURS, en
// production comprise, ou il designe la machine du gestionnaire sur un port ou
// rien n'ecoute : le serveur est sur 3001, pas sur 5000. Le bouton « Voir »
// ouvrait donc un onglet mort, a tous les coups, pour toutes les ecoles, sans
// un mot dans l'application. Verifie le 04/10/2026 dans le paquet compile qui
// tourne en production : « localhost:5000 » y etait bien present.
//
// Le chemin relatif suit le domaine de la page — csap1 depuis csap1, csap2
// depuis csap2 — et nginx route /uploads vers Express sur les trois
// sous-domaines. C'est la seule forme correcte. Le meme correctif a ete
// applique a `SectionExamen.jsx`, qui porte la meme cellule copiee.
const CandidatsLibresSection = () => {
  const { notification, notifier, masquer } = useNotification();
  const { triggerRefresh } = useExamensContext();
  
  const [candidats, setCandidats] = useState([]);
  const [selectedCandidat, setSelectedCandidat] = useState(null);
  const [showCandidatFile, setShowCandidatFile] = useState(false);
  const [detailsCandidat, setDetailsCandidat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  // Deux actions distinctes sur une meme ligne : les confondre faisait
  // tourner le bouton de depot quand on cliquait seulement « Recue ».
  // { pieceId, recue } : sans le sens de l'action, le bouton « Recue »
  // s'animait aussi quand on cliquait « Non ».
  // Les ecritures des modals : mutuellement exclusives, un seul drapeau.
  const [ecritureEnCours, setEcritureEnCours] = useState(false);
  const [receptionEnCours, setReceptionEnCours] = useState(null);
  const [depotEnCours, setDepotEnCours] = useState(null);
  
  const [page, setPage] = useState(1);
  const PAR_PAGE = 25;
  const [searchTerm, setSearchTerm] = useState('');
  const [examenFilter, setExamenFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDeleteDocModal, setShowDeleteDocModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  

  const [candidatForm, setCandidatForm] = useState({
    nom: '',
    prenom: '',
    sexe: 'M',
    date_naissance: '',
    lieu_naissance: '',
    nationalite: 'Ivoirienne',
    telephone: '',
    email: '',
    adresse: '',
    dernier_etablissement: '',
    examen_type: 'CEPE',
    serie: ''
  });

  const [paiementForm, setPaiementForm] = useState({
    montant_paye: '',
    mode_paiement: 'especes',
    reference_paiement: '',
    notes: ''
  });

  const [exportEnCours, setExportEnCours] = useState(false);

  // Le bouton « Exporter » n'avait aucun onClick. Il passe par le socle PDF du
  // projet, et emporte les filtres de l'ecran : une liste filtree doit dire
  // qu'elle l'est, sinon elle se lit comme la liste complete.
  const handleExporter = async () => {
    setExportEnCours(true);
    try {
      const reponse = await api.examens.exporterListePDF({
        candidatsLibres: true,
        examenType: examenFilter !== 'all' ? examenFilter : null,
        filtreStatut: statusFilter !== 'all'
          ? STATUTS_DOSSIER.find((s) => s.valeur === statusFilter)?.libelle
          : null,
        filtreRecherche: searchTerm.trim() || null
      });
      const nom = await telechargerPdf(reponse, 'Liste_candidats_libres.pdf');
      showToast(`Liste exportée : ${nom}`);
    } catch (erreur) {
      showToast(erreur.message || "L'export a échoué", 'error');
    } finally {
      setExportEnCours(false);
    }
  };

  // Le rendu passe desormais par ToastPortal (pour sortir de la pile d'un
  // Dialog) et bascule en LongToast quand le message du serveur est long.
  const showToast = (message, type = 'success') => notifier(message, type);

  // Calculer le statut d'un candidat
  const calculateStatut = (candidat) => {
    const docsComplets = candidat.documents_fournis_count >= candidat.documents_requis_count;
    const paiementComplet = candidat.statut_paiement === 'paye';

    if (docsComplets && paiementComplet) return 'complete';
    if (docsComplets || paiementComplet) return 'pending';
    return 'incomplete';
  };

  // Charger tous les candidats libres
  const loadCandidats = async () => {
    setLoading(true);
    try {
      const result = await api.examens.getAllCandidatsLibres();
      if (result.success) {
        setCandidats(result.data);
      } else {
        showToast('Erreur lors du chargement des candidats', 'error');
      }
    } catch (error) {
      console.error('Erreur chargement candidats:', error);
      showToast('Erreur lors du chargement', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCandidats();
  }, []);

  // Constater la reception d'une piece, sans fichier.
  const handleMarquerReception = async (documentRequisId, recue) => {
    setReceptionEnCours({ pieceId: documentRequisId, recue });
    try {
      const result = await api.examens.marquerReceptionPiece({
        candidat_libre_id: selectedCandidat.id,
        document_requis_id: documentRequisId,
        recue
      });

      if (result.success) {
        const details = await api.examens.getDetailsCandidatLibre(selectedCandidat.id);
        if (details.success) setDetailsCandidat(details.data);

        setCandidats((prev) => prev.map((c) => c.id === selectedCandidat.id
          ? {
            ...c,
            documents_fournis_count: Math.max(
              0, (c.documents_fournis_count || 0) + (recue ? 1 : -1)
            )
          }
          : c));

        triggerRefresh();
        showToast(result.message || (recue ? 'Piece marquee comme recue' : 'Piece retiree'));
      } else {
        showToast(result.message || result.error || 'Operation impossible', 'error');
      }
    } catch (erreur) {
      showToast(erreur.message || 'Operation impossible', 'error');
    } finally {
      setReceptionEnCours(null);
    }
  };

  // Filtrer les candidats
  const filteredCandidats = candidats.filter(candidat => {
    const matchesSearch = candidat.nom.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          candidat.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          candidat.telephone.includes(searchTerm);
    
    const matchesExamen = examenFilter === 'all' || candidat.examen_type === examenFilter;
    
    let matchesStatus = true;
    if (statusFilter !== 'all') {
      const statut = calculateStatut(candidat);
      matchesStatus = statut === statusFilter;
    }
    
    return matchesSearch && matchesExamen && matchesStatus;
  });

  const nombrePages = Math.max(1, Math.ceil(filteredCandidats.length / PAR_PAGE));
  const candidatsPage = filteredCandidats.slice((page - 1) * PAR_PAGE, page * PAR_PAGE);

  // Un filtre qui reduit la liste ramene a la premiere page.
  useEffect(() => {
    setPage(1);
  }, [searchTerm, examenFilter, statusFilter]);

  // Ouvrir le modal d'ajout
  const handleOpenAddModal = () => {
    setCandidatForm({
      nom: '',
      prenom: '',
      sexe: 'M',
      date_naissance: '',
      lieu_naissance: '',
      nationalite: 'Ivoirienne',
      telephone: '',
      email: '',
      adresse: '',
      dernier_etablissement: '',
      examen_type: 'CEPE',
      serie: ''
    });
    setShowAddModal(true);
  };

  // Créer un candidat libre
  const handleCreateCandidat = async () => {
    // Validation
    if (!candidatForm.nom || !candidatForm.prenom || !candidatForm.date_naissance || 
        !candidatForm.lieu_naissance || !candidatForm.telephone) {
      showToast('Veuillez remplir tous les champs obligatoires', 'error');
      return;
    }

    setEcritureEnCours(true);
    try {
      const result = await api.examens.inscrireCandidatLibre(candidatForm);
      if (result.success) {
        showToast('Candidat libre créé avec succès');
        setShowAddModal(false);
        loadCandidats();
        triggerRefresh();
      } else {
        showToast('Erreur: ' + result.message, 'error');
      }
    } catch (error) {
      showToast('Erreur lors de la création', 'error');
    } finally {
      setEcritureEnCours(false);
    }
  };

  // Voir le dossier d'un candidat
  const handleViewCandidat = async (candidat) => {
    setSelectedCandidat(candidat);
    setShowCandidatFile(true);
    setLoadingDetails(true);

    try {
      const result = await api.examens.getDetailsCandidatLibre(candidat.id);
      if (result.success) {
        setDetailsCandidat(result.data);
      } else {
        showToast('Erreur lors du chargement des détails', 'error');
        setShowCandidatFile(false);
      }
    } catch (error) {
      console.error('Erreur chargement détails:', error);
      showToast('Erreur lors du chargement', 'error');
      setShowCandidatFile(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Ouvrir le modal de modification
  const handleOpenEditModal = () => {
    setCandidatForm({
      nom: detailsCandidat.candidat.nom,
      prenom: detailsCandidat.candidat.prenom,
      sexe: detailsCandidat.candidat.sexe,
      date_naissance: detailsCandidat.candidat.date_naissance?.split('T')[0],
      lieu_naissance: detailsCandidat.candidat.lieu_naissance,
      nationalite: detailsCandidat.candidat.nationalite,
      telephone: detailsCandidat.candidat.telephone,
      email: detailsCandidat.candidat.email || '',
      adresse: detailsCandidat.candidat.adresse || '',
      dernier_etablissement: detailsCandidat.candidat.dernier_etablissement || '',
      serie: detailsCandidat.candidat.serie || ''
    });
    setShowEditModal(true);
  };

  // Modifier un candidat
  const handleUpdateCandidat = async () => {
    setEcritureEnCours(true);
    try {
      const result = await api.examens.modifierCandidatLibre(selectedCandidat.id, candidatForm);
      if (result.success) {
        showToast('Candidat modifié avec succès');
        setShowEditModal(false);
        
        // Recharger les détails
        const details = await api.examens.getDetailsCandidatLibre(selectedCandidat.id);
        if (details.success) {
          setDetailsCandidat(details.data);
        }
        
        loadCandidats();
        triggerRefresh();
      } else {
        showToast('Erreur: ' + result.message, 'error');
      }
    } catch (error) {
      showToast('Erreur lors de la modification', 'error');
    } finally {
      setEcritureEnCours(false);
    }
  };

  // Supprimer un candidat
  const handleDeleteCandidat = async () => {
    setEcritureEnCours(true);
    try {
      const result = await api.examens.supprimerCandidatLibre(selectedCandidat.id);
      if (result.success) {
        showToast('Candidat supprimé avec succès');
        setShowDeleteModal(false);
        setShowCandidatFile(false);
        loadCandidats();
        triggerRefresh();
      } else {
        showToast('Erreur: ' + result.message, 'error');
      }
    } catch (error) {
      showToast('Erreur lors de la suppression', 'error');
    } finally {
      setEcritureEnCours(false);
    }
  };

  // Upload de document
  const handleUploadDocument = async (documentRequisId, file) => {
    // La piece concernee, pas toutes : un drapeau unique faisait tourner les
    // boutons des lignes auxquelles on n'avait pas touche.
    setDepotEnCours(documentRequisId);
    try {
      const result = await api.examens.uploadDocumentCandidatLibre(
        selectedCandidat.id, 
        documentRequisId, 
        file
      );
      
      if (result.success) {
        showToast('Document uploadé avec succès');
        
        // Recharger les détails
        const details = await api.examens.getDetailsCandidatLibre(selectedCandidat.id);
        if (details.success) {
          setDetailsCandidat(details.data);
        }
        
        // Mettre à jour localement
        setCandidats(prevCandidats => 
          prevCandidats.map(c => 
            c.id === selectedCandidat.id 
              ? { ...c, documents_fournis_count: c.documents_fournis_count + 1 }
              : c
          )
        );
        
        triggerRefresh();
      } else {
        showToast('Erreur: ' + result.message, 'error');
      }
    } catch (error) {
      showToast('Erreur lors de l\'upload', 'error');
    } finally {
      setDepotEnCours(null);
    }
  };

  // Supprimer un document
  const handleDeleteDocumentClick = (doc) => {
    setDocumentToDelete(doc);
    setShowDeleteDocModal(true);
  };

  const handleConfirmDeleteDocument = async () => {
    if (documentToDelete) {
      setEcritureEnCours(true);
      try {
        const result = await api.examens.deleteDocument(documentToDelete.id);
        if (result.success) {
          showToast('Document supprimé avec succès');
          
          // Recharger les détails
          const details = await api.examens.getDetailsCandidatLibre(selectedCandidat.id);
          if (details.success) {
            setDetailsCandidat(details.data);
          }
          
          // Mettre à jour localement
          setCandidats(prevCandidats => 
            prevCandidats.map(c => 
              c.id === selectedCandidat.id 
                ? { ...c, documents_fournis_count: Math.max(0, c.documents_fournis_count - 1) }
                : c
            )
          );
          
          triggerRefresh();
        } else {
          showToast('Erreur: ' + result.message, 'error');
        }
      } catch (error) {
        showToast('Erreur lors de la suppression', 'error');
      }
    }
    setEcritureEnCours(false);
    setShowDeleteDocModal(false);
    setDocumentToDelete(null);
  };

  // Ouvrir le modal de paiement
  const handleOpenPaymentModal = () => {
    if (detailsCandidat?.paiement) {
      const reste = parseFloat(detailsCandidat.paiement.montant_attendu) - 
                    parseFloat(detailsCandidat.paiement.montant_paye || 0);
      setPaiementForm({
        montant_paye: reste > 0 ? reste : '',
        mode_paiement: 'especes',
        reference_paiement: '',
        notes: ''
      });
    }
    setShowPaymentModal(true);
  };

  // Enregistrer un paiement
  const handleSavePayment = async () => {
    if (!paiementForm.montant_paye || parseFloat(paiementForm.montant_paye) <= 0) {
      showToast('Veuillez saisir un montant valide', 'error');
      return;
    }

    setEcritureEnCours(true);
    try {
      const result = await api.examens.enregistrerPaiement({
        candidat_libre_id: selectedCandidat.id,
        examen_type: selectedCandidat.examen_type,
        montant_paye: parseFloat(paiementForm.montant_paye),
        mode_paiement: paiementForm.mode_paiement,
        reference_paiement: paiementForm.reference_paiement,
        notes: paiementForm.notes
      });

      if (result.success) {
        // Le serveur renvoie le reste dû : l'annoncer évite de rouvrir le
        // dossier pour savoir si le candidat est en règle.
        const reste = Number(result.data?.reste_du ?? 0);
        showToast(
          reste > 0
            ? `Paiement enregistré. Reste dû : ${reste.toLocaleString('fr-FR')} FCFA`
            : 'Paiement enregistré. Frais soldés.'
        );
        setShowPaymentModal(false);
        
        // Recharger les détails
        const details = await api.examens.getDetailsCandidatLibre(selectedCandidat.id);
        if (details.success) {
          setDetailsCandidat(details.data);
        }
        
        // Mettre à jour localement
        setCandidats(prevCandidats => 
          prevCandidats.map(c => 
            c.id === selectedCandidat.id 
              ? { 
                  ...c, 
                  statut_paiement: details.data.paiement?.statut || 'partiel',
                  montant_paye: details.data.paiement?.montant_paye || 0
                }
              : c
          )
        );
        
        triggerRefresh();
        
        setPaiementForm({
          montant_paye: '',
          mode_paiement: 'especes',
          reference_paiement: '',
          notes: ''
        });
      } else {
        // Le serveur refuse un versement supérieur au reste dû ou des frais
        // déjà soldés : son message est plus utile qu'un libellé générique.
        showToast(result.message || result.error || 'Paiement refusé', 'error');
      }
    } catch (error) {
      showToast(error.message || "Erreur lors de l'enregistrement", 'error');
    } finally {
      setEcritureEnCours(false);
    }
  };

  // Fonctions utilitaires
  const getStatusBadge = (status) => {
    const styles = {
      complete: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      incomplete: 'bg-red-100 text-red-800'
    };
    
    const labels = {
      complete: 'Complet',
      pending: 'En attente',
      incomplete: 'Incomplet'
    };
    
    return (
      <span className={`inline-flex items-center justify-center w-[132px] h-6 rounded-full text-[10px] font-semibold whitespace-nowrap ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getExamenBadge = (examen) => {
    const colors = {
      CEPE: 'bg-blue-100 text-blue-800',
      BEPC: 'bg-purple-100 text-purple-800',
      BAC: 'bg-indigo-100 text-indigo-800'
    };
    
    return (
      <span className={`inline-flex items-center justify-center w-[132px] h-6 rounded-full text-[10px] font-semibold whitespace-nowrap ${colors[examen]}`}>
        {examen}
      </span>
    );
  };

  // On ne vide l'ecran que tant qu'il n'y a rien a montrer ; au-dela, le
  // chargement ne vide plus rien : la liste reste lisible.
  const premierChargement = loading && candidats.length === 0;

  return (
    <div>
      {/* Liste des candidats libres */}
      {!showCandidatFile && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-[#246990]">Candidats Libres</h2>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleExporter}
                disabled={exportEnCours || filteredCandidats.length === 0}
                className="flex items-center gap-2 bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:opacity-50"
              >
                {exportEnCours
                  ? <Loader2 size={16} className="animate-spin" />
                  : <Download size={16} />}
                Exporter
              </Button>
              <Button
                size="sm"
                onClick={handleOpenAddModal}
                className="flex items-center gap-2 bg-[#319190] text-white rounded-md hover:bg-[#319190]/90 transition-colors"
              >
                <UserPlus size={16} />
                Nouveau candidat
              </Button>
            </div>
          </div>

          {/* Filtres */}
          <div className="flex gap-3 mb-6">
            <div className="relative flex-1">
              <Input
                placeholder="Rechercher un candidat..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
              <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
            </div>
            
            <ChampSelect
              valeur={examenFilter}
              onChange={setExamenFilter}
              options={EXAMENS_FILTRE}
              className="w-52"
            />
            
            <ChampSelect
              valeur={statusFilter}
              onChange={setStatusFilter}
              options={STATUTS_DOSSIER}
              className="w-52"
            />
          </div>

          {/* Tableau des candidats */}
          <Card className="relative overflow-hidden">

            {premierChargement ? (
              <LoadingSpinner message="Chargement des candidats libres…" />
            ) : filteredCandidats.length === 0 ? (
              <EtatVide
                icone={UserPlus}
                titre={candidats.length === 0
                  ? 'Aucun candidat libre'
                  : 'Aucun candidat ne correspond aux filtres'}
                description={candidats.length === 0
                  ? "Un candidat libre est une personne qui présente l'examen sans venir de l'école."
                  : "Élargissez la recherche, ou retirez le filtre d'examen ou de statut."}
                action={candidats.length === 0 ? (
                  <Button
                    size="sm"
                    onClick={handleOpenAddModal}
                    className="gap-2 bg-[#246990] text-white hover:bg-[#1f5a7a]"
                  >
                    <UserPlus size={16} />
                    Inscrire un candidat
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSearchTerm('');
                      setExamenFilter('all');
                      setStatusFilter('all');
                    }}
                  >
                    Retirer les filtres
                  </Button>
                )}
              />
            ) : (
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Candidat
                      </th>
                      <th className="px-6 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Examen
                      </th>
                      <th className="px-6 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Documents
                      </th>
                      <th className="px-6 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Paiement
                      </th>
                      <th className="px-6 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Statut
                      </th>
                      {/* Largeur fixe : sinon les boutons se decalent d'une ligne a l'autre. */}
                      <th className="w-[150px] px-6 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {candidatsPage.map((candidat) => {
                      const statut = calculateStatut(candidat);
                      return (
                        <tr key={candidat.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div>
                              <div className="font-medium text-gray-900">
                                {candidat.nom} {candidat.prenom}
                              </div>
                              <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                                <Phone size={12} />
                                {candidat.telephone}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {getExamenBadge(candidat.examen_type)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                            {candidat.documents_fournis_count}/{candidat.documents_requis_count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`inline-flex items-center justify-center w-[132px] h-6 rounded-full text-[10px] font-semibold whitespace-nowrap ${
                              candidat.statut_paiement === 'paye' ? 'bg-green-100 text-green-800' :
                              candidat.statut_paiement === 'partiel' ? 'bg-orange-100 text-orange-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {candidat.statut_paiement === 'paye' ? 'Payé' :
                               candidat.statut_paiement === 'partiel' ? 'Partiel' :
                               'Non payé'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {getStatusBadge(statut)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewCandidat(candidat)}
                              className="border-[#246990] text-[#246990] hover:bg-[#246990] hover:text-white"
                            >
                              <FileText size={16} className="mr-1" />
                              Voir le dossier
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
            )}
          </Card>

          <div className="mt-4">
            <Pagination
              page={page}
              nombrePages={nombrePages}
              total={filteredCandidats.length}
              parPage={PAR_PAGE}
              onChangerPage={setPage}
              libelleElement="candidat"
            />
          </div>
        </div>
      )}

      {/* Dossier du candidat */}
      {showCandidatFile && selectedCandidat && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCandidatFile(false)}
                className="mb-2"
              >
                ← Retour à la liste
              </Button>
              <h2 className="text-xl font-semibold text-[#246990]">
                Dossier {selectedCandidat.examen_type} - {selectedCandidat.nom} {selectedCandidat.prenom}
              </h2>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleOpenEditModal}
                className="flex items-center gap-2 bg-[#319190] text-white rounded-md hover:bg-[#319190]/90 transition-colors"
              >
                <Edit size={16} />
                Modifier
              </Button>
              <Button
                size="sm"
                onClick={() => setShowDeleteModal(true)}
                className="flex items-center gap-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                <Trash2 size={16} />
                Supprimer
              </Button>
            </div>
          </div>

          {loadingDetails ? (
            <LoadingSpinner message="Chargement du dossier…" />
          ) : detailsCandidat ? (
            <div className="space-y-6">
              {/* Informations personnelles */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#246990]">Informations personnelles</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Nom complet</p>
                        <p className="font-medium">{detailsCandidat.candidat.nom} {detailsCandidat.candidat.prenom}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Date de naissance</p>
                        <p className="font-medium">
                          {new Date(detailsCandidat.candidat.date_naissance).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin size={16} className="text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Lieu de naissance</p>
                        <p className="font-medium">{detailsCandidat.candidat.lieu_naissance}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-600">Téléphone</p>
                        <p className="font-medium">{detailsCandidat.candidat.telephone}</p>
                      </div>
                    </div>
                    {detailsCandidat.candidat.email && (
                      <div className="flex items-center gap-2">
                        <Mail size={16} className="text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Email</p>
                          <p className="font-medium">{detailsCandidat.candidat.email}</p>
                        </div>
                      </div>
                    )}
                    {detailsCandidat.candidat.dernier_etablissement && (
                      <div className="flex items-center gap-2">
                        <School size={16} className="text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-600">Dernier établissement</p>
                          <p className="font-medium">{detailsCandidat.candidat.dernier_etablissement}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Documents requis */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-[#246990]">Documents requis</CardTitle>
                  <CardDescription>
                    Liste des documents nécessaires pour l'inscription au {selectedCandidat.examen_type}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            Pièce exigée
                          </th>
                          <th className="w-[220px] px-6 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            Réception
                          </th>
                          <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            Fichier joint <span className="font-normal normal-case">(facultatif)</span>
                          </th>
                          <th className="w-[150px] px-6 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {detailsCandidat.documents_requis.map((docRequis) => {
                          const docFourni = detailsCandidat.documents_fournis.find(
                            df => df.document_requis_id === docRequis.id
                          );
                          const recue = Boolean(docFourni);
                          const avecFichier = Boolean(docFourni?.nom_fichier);
                          const receptionEnCoursIci = receptionEnCours?.pieceId === docRequis.id;
                          const attendRecue = receptionEnCoursIci && receptionEnCours.recue;
                          const attendRetrait = receptionEnCoursIci && !receptionEnCours.recue;
                          const depotEnCoursIci = depotEnCours === docRequis.id;

                          return (
                            <tr key={docRequis.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm">
                                <p className="font-semibold text-gray-900">
                                  {docRequis.nom_document}
                                  {docRequis.obligatoire && <span className="ml-1 text-red-500">*</span>}
                                </p>
                                {docRequis.description && (
                                  <p className="mt-1 text-xs text-gray-500">{docRequis.description}</p>
                                )}
                              </td>

                              {/* Réception : l'action principale. */}
                              <td className="px-6 py-4 text-center">
                                <div className="inline-flex overflow-hidden rounded-lg border border-gray-200">
                                  <button
                                    type="button"
                                    disabled={receptionEnCoursIci}
                                    onClick={() => handleMarquerReception(docRequis.id, true)}
                                    className={`inline-flex h-8 w-[86px] items-center justify-center gap-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${recue
                                      ? 'bg-[#246990] text-white'
                                      : 'bg-white text-gray-600 hover:bg-gray-50'
                                      }`}
                                  >
                                    {attendRecue
                                      ? <Loader2 size={13} className="animate-spin" />
                                      : <CheckCircle size={13} />}
                                    Reçue
                                  </button>
                                  <button
                                    type="button"
                                    disabled={receptionEnCoursIci}
                                    onClick={() => handleMarquerReception(docRequis.id, false)}
                                    className={`inline-flex h-8 w-[86px] items-center justify-center gap-1.5 border-l border-gray-200 text-xs font-semibold transition-colors disabled:opacity-50 ${!recue
                                      ? 'bg-gray-100 text-gray-700'
                                      : 'bg-white text-gray-500 hover:bg-gray-50'
                                      }`}
                                  >
                                    {attendRetrait
                                      ? <Loader2 size={13} className="animate-spin" />
                                      : <XCircle size={13} />}
                                    Non
                                  </button>
                                </div>
                                {recue && !avecFichier && (
                                  <p className="mt-1 text-[10px] text-gray-400">Non numérisée</p>
                                )}
                              </td>

                              <td className="px-6 py-4 text-sm">
                                {avecFichier ? (
                                  <div>
                                    <div className="flex items-center gap-1.5">
                                      <FileText size={16} className="text-gray-600" />
                                      <span className="text-sm">{docFourni.nom_fichier}</span>
                                    </div>
                                    {docFourni.date_depot && (
                                      <p className="mt-1 text-xs text-gray-500">
                                        {new Date(docFourni.date_depot).toLocaleString('fr-FR')}
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <div>
                                    <input
                                      type="file"
                                      id={`file-cl-${docRequis.id}`}
                                      className="hidden"
                                      accept=".pdf,.jpg,.jpeg,.png"
                                      onChange={(e) => {
                                        if (e.target.files[0]) {
                                          handleUploadDocument(docRequis.id, e.target.files[0]);
                                        }
                                      }}
                                      disabled={depotEnCoursIci}
                                    />
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => document.getElementById(`file-cl-${docRequis.id}`).click()}
                                      disabled={depotEnCoursIci}
                                      className="gap-1.5"
                                    >
                                      {depotEnCoursIci
                                        ? <Loader2 size={14} className="animate-spin" />
                                        : <Upload size={14} />}
                                      Joindre un fichier
                                    </Button>
                                  </div>
                                )}
                              </td>

                              <td className="px-6 py-4 text-center">
                                {avecFichier && (
                                  <div className="flex justify-center gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => window.open(docFourni.chemin_fichier, '_blank')}
                                      className="text-[#246990] hover:bg-[#eef7fb]"
                                    >
                                      <Eye size={14} />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDeleteDocumentClick(docFourni)}
                                      className="text-red-600 hover:bg-red-50"
                                    >
                                      <Trash2 size={14} />
                                    </Button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Frais d'inscription */}
              {detailsCandidat.paiement && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-[#246990]">Frais d'inscription</CardTitle>
                        <CardDescription>Suivi des paiements pour l'examen {selectedCandidat.examen_type}</CardDescription>
                      </div>
                      <Button
                        size="sm"
                        onClick={handleOpenPaymentModal}
                        className={`text-white transition-colors ${
                          detailsCandidat.paiement.statut === 'paye'
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-[#c66d59] hover:bg-[#ae7467]/90'
                        }`}
                        disabled={detailsCandidat.paiement.statut === 'paye'}
                      >
                        <CreditCard size={16} className="mr-2" />
                        Enregistrer un paiement
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                      <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Montant attendu</p>
                        <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900">
                          {formaterFCFA(detailsCandidat.paiement.montant_attendu)}
                        </p>
                      </div>
                      <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Déjà versé</p>
                        <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900">
                          {formaterFCFA(detailsCandidat.paiement.montant_paye)}
                        </p>
                      </div>
                      {/* Le seul chiffre en gras fort : c'est lui qui commande l'action. */}
                      <div className="rounded-md border border-[#246990]/20 bg-[#eef7fb] p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Reste dû</p>
                        <p className="mt-1 text-2xl font-bold tabular-nums text-[#246990]">
                          {formaterFCFA(
                            Number(detailsCandidat.paiement.montant_attendu || 0)
                            - Number(detailsCandidat.paiement.montant_paye || 0)
                          )}
                        </p>
                      </div>
                      <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Statut</p>
                        <span className={`mt-2 inline-flex h-6 w-[132px] items-center justify-center rounded-full text-[10px] font-semibold whitespace-nowrap ${
                          detailsCandidat.paiement.statut === 'paye' ? 'bg-green-100 text-green-800'
                            : detailsCandidat.paiement.statut === 'partiel' ? 'bg-orange-100 text-orange-800'
                              : 'bg-red-100 text-red-800'
                        }`}>
                          {detailsCandidat.paiement.statut === 'paye' ? 'Payé'
                            : detailsCandidat.paiement.statut === 'partiel' ? 'Partiel'
                              : 'Non payé'}
                        </span>
                      </div>
                    </div>

                    {detailsCandidat.paiement.date_paiement && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm font-medium text-gray-700">Dernier paiement</p>
                          {detailsCandidat.paiement.statut === 'paye' && (
                            <span className="flex items-center text-green-600 text-sm">
                              <CheckCircle size={16} className="mr-1" />
                              Paiement complet
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          {detailsCandidat.paiement.date_paiement && (
                            <div className="flex items-start">
                              <Calendar size={16} className="mr-2 text-gray-400 mt-0.5" />
                              <div>
                                <span className="text-gray-600 block">Date de paiement</span>
                                <span className="font-medium">
                                  {new Date(detailsCandidat.paiement.date_paiement).toLocaleString('fr-FR', {
                                    dateStyle: 'long',
                                    timeStyle: 'short'
                                  })}
                                </span>
                              </div>
                            </div>
                          )}
                          {detailsCandidat.paiement.mode_paiement && (
                            <div className="flex items-start">
                              <CreditCard size={16} className="mr-2 text-gray-400 mt-0.5" />
                              <div>
                                <span className="text-gray-600 block">Mode de paiement</span>
                                <span className="font-medium capitalize">
                                  {detailsCandidat.paiement.mode_paiement.replace('_', ' ')}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Modal Ajouter un candidat */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="px-5 py-4 border-b">
            <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-gray-900">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#246990] text-white">
                <UserPlus size={20} />
              </span>
              Nouveau candidat libre
            </DialogTitle>
            <DialogDescription>
              Remplissez les informations du candidat libre
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto px-5 py-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="nom">Nom *</Label>
              <Input
                id="nom"
                value={candidatForm.nom}
                onChange={(e) => setCandidatForm({...candidatForm, nom: e.target.value})}
                placeholder="Ex: KOUASSI"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prenom">Prénom *</Label>
              <Input
                id="prenom"
                value={candidatForm.prenom}
                onChange={(e) => setCandidatForm({...candidatForm, prenom: e.target.value})}
                placeholder="Ex: Jean"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sexe">Sexe *</Label>
              <ChampSelect
                id="sexe"
                valeur={candidatForm.sexe}
                onChange={(v) => setCandidatForm({ ...candidatForm, sexe: v })}
                options={SEXES}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="date_naissance">Date de naissance *</Label>
              <Input
                id="date_naissance"
                type="date"
                value={candidatForm.date_naissance}
                onChange={(e) => setCandidatForm({...candidatForm, date_naissance: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lieu_naissance">Lieu de naissance *</Label>
              <Input
                id="lieu_naissance"
                value={candidatForm.lieu_naissance}
                onChange={(e) => setCandidatForm({...candidatForm, lieu_naissance: e.target.value})}
                placeholder="Ex: Abidjan"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="nationalite">Nationalité</Label>
              <Input
                id="nationalite"
                value={candidatForm.nationalite}
                onChange={(e) => setCandidatForm({...candidatForm, nationalite: e.target.value})}
                placeholder="Ex: Ivoirienne"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="telephone">Téléphone *</Label>
              <Input
                id="telephone"
                value={candidatForm.telephone}
                onChange={(e) => setCandidatForm({...candidatForm, telephone: e.target.value})}
                placeholder="Ex: 0707070707"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={candidatForm.email}
                onChange={(e) => setCandidatForm({...candidatForm, email: e.target.value})}
                placeholder="Ex: candidat@email.com"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="adresse">Adresse</Label>
              <Input
                id="adresse"
                value={candidatForm.adresse}
                onChange={(e) => setCandidatForm({...candidatForm, adresse: e.target.value})}
                placeholder="Ex: Cocody, Angré"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dernier_etablissement">Dernier établissement</Label>
              <Input
                id="dernier_etablissement"
                value={candidatForm.dernier_etablissement}
                onChange={(e) => setCandidatForm({...candidatForm, dernier_etablissement: e.target.value})}
                placeholder="Ex: Collège Moderne"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="examen_type">Type d'examen *</Label>
              <ChampSelect
                id="examen_type"
                valeur={candidatForm.examen_type}
                onChange={(v) => setCandidatForm({ ...candidatForm, examen_type: v })}
                options={EXAMENS_CHOIX}
                className="w-full"
              />
            </div>

            {candidatForm.examen_type === 'BAC' && (
              <div className="space-y-2">
                <Label htmlFor="serie">Série</Label>
                <Input
                  id="serie"
                  value={candidatForm.serie}
                  onChange={(e) => setCandidatForm({...candidatForm, serie: e.target.value})}
                  placeholder="Ex: A, C, D..."
                />
              </div>
            )}
            </div>
          </div>
          
          <DialogFooter className="px-5 py-4 border-t bg-gray-50 sm:justify-end gap-2">
            <Button 
              variant="outline"
              className={BOUTON_PIED_CLASSES}
              onClick={() => setShowAddModal(false)}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleCreateCandidat}
              disabled={ecritureEnCours}
              className={`${BOUTON_PIED_CLASSES} bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:opacity-50`}
            >
              {ecritureEnCours
                ? <Loader2 size={16} className="animate-spin" />
                : <CheckCircle size={16} />}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Modifier un candidat */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="px-5 py-4 border-b">
            <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-gray-900">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#246990] text-white">
                <Edit size={20} />
              </span>
              Modifier le candidat
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto px-5 py-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="edit_nom">Nom *</Label>
              <Input
                id="edit_nom"
                value={candidatForm.nom}
                onChange={(e) => setCandidatForm({...candidatForm, nom: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_prenom">Prénom *</Label>
              <Input
                id="edit_prenom"
                value={candidatForm.prenom}
                onChange={(e) => setCandidatForm({...candidatForm, prenom: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_sexe">Sexe *</Label>
              <ChampSelect
                id="sexe"
                valeur={candidatForm.sexe}
                onChange={(v) => setCandidatForm({ ...candidatForm, sexe: v })}
                options={SEXES}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_date_naissance">Date de naissance *</Label>
              <Input
                id="edit_date_naissance"
                type="date"
                value={candidatForm.date_naissance}
                onChange={(e) => setCandidatForm({...candidatForm, date_naissance: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_lieu_naissance">Lieu de naissance *</Label>
              <Input
                id="edit_lieu_naissance"
                value={candidatForm.lieu_naissance}
                onChange={(e) => setCandidatForm({...candidatForm, lieu_naissance: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_nationalite">Nationalité</Label>
              <Input
                id="edit_nationalite"
                value={candidatForm.nationalite}
                onChange={(e) => setCandidatForm({...candidatForm, nationalite: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_telephone">Téléphone *</Label>
              <Input
                id="edit_telephone"
                value={candidatForm.telephone}
                onChange={(e) => setCandidatForm({...candidatForm, telephone: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_email">Email</Label>
              <Input
                id="edit_email"
                type="email"
                value={candidatForm.email}
                onChange={(e) => setCandidatForm({...candidatForm, email: e.target.value})}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="edit_adresse">Adresse</Label>
              <Input
                id="edit_adresse"
                value={candidatForm.adresse}
                onChange={(e) => setCandidatForm({...candidatForm, adresse: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_dernier_etablissement">Dernier établissement</Label>
              <Input
                id="edit_dernier_etablissement"
                value={candidatForm.dernier_etablissement}
                onChange={(e) => setCandidatForm({...candidatForm, dernier_etablissement: e.target.value})}
              />
            </div>

            {detailsCandidat?.candidat.examen_type === 'BAC' && (
              <div className="space-y-2">
                <Label htmlFor="edit_serie">Série</Label>
                <Input
                  id="edit_serie"
                  value={candidatForm.serie}
                  onChange={(e) => setCandidatForm({...candidatForm, serie: e.target.value})}
                />
              </div>
            )}
            </div>
          </div>
          
          <DialogFooter className="px-5 py-4 border-t bg-gray-50 sm:justify-end gap-2">
            <Button 
              variant="outline"
              className={BOUTON_PIED_CLASSES}
              onClick={() => setShowEditModal(false)}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleUpdateCandidat}
              disabled={ecritureEnCours}
              className={`${BOUTON_PIED_CLASSES} bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:opacity-50`}
            >
              {ecritureEnCours
                ? <Loader2 size={16} className="animate-spin" />
                : <CheckCircle size={16} />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmation suppression candidat */}
      {/* ConfirmDialog plutot qu'un Dialog monte a la main : c'est le composant
          de confirmation du projet, et il aligne les deux boutons de pied. */}
      <ConfirmDialog
        isOpen={showDeleteModal}
        isLoading={ecritureEnCours}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteCandidat}
        type="error"
        title="Supprimer ce candidat ?"
        description={`${selectedCandidat?.prenom || ''} ${selectedCandidat?.nom || ''} sera définitivement supprimé, avec toutes ses pièces déposées. Cette action est irréversible.`.trim()}
        confirmText="Supprimer définitivement"
        cancelText="Annuler"
      />

      {/* Modal Enregistrer un paiement */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="w-[95vw] max-w-3xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="px-5 py-4 border-b">
            <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-gray-900">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#246990] text-white">
                <CreditCard size={20} />
              </span>
              Enregistrer un paiement
            </DialogTitle>
            <DialogDescription>
              Paiement des frais d'examen pour {selectedCandidat?.nom} {selectedCandidat?.prenom}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {/* Ce qui reste du, en tete : c'est le chiffre qui commande la saisie. */}
            {detailsCandidat?.paiement && (
              <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Attendu</p>
                    <p className="font-semibold tabular-nums text-gray-900">
                      {formaterFCFA(detailsCandidat.paiement.montant_attendu)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Déjà versé</p>
                    <p className="font-semibold tabular-nums text-gray-900">
                      {formaterFCFA(detailsCandidat.paiement.montant_paye)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Reste dû</p>
                    <p className="font-semibold tabular-nums text-[#246990]">
                      {formaterFCFA(
                        Number(detailsCandidat.paiement.montant_attendu || 0)
                        - Number(detailsCandidat.paiement.montant_paye || 0)
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="montant_paye" className="text-sm text-gray-700">
                  Montant versé (FCFA) *
                </Label>
                <Input
                  id="montant_paye"
                  type="number"
                  min="0"
                  placeholder="Ex: 15000"
                  value={paiementForm.montant_paye}
                  onChange={(e) => setPaiementForm({ ...paiementForm, montant_paye: e.target.value })}
                  className="h-9 rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mode_paiement" className="text-sm text-gray-700">
                  Mode de paiement *
                </Label>
                <ChampSelect
                  id="mode_paiement"
                  valeur={paiementForm.mode_paiement}
                  onChange={(v) => setPaiementForm({ ...paiementForm, mode_paiement: v })}
                  options={MODES_PAIEMENT}
                  className="w-full"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="reference_paiement" className="text-sm text-gray-700">
                  Référence de paiement
                </Label>
                <Input
                  id="reference_paiement"
                  placeholder="Ex: CHQ123456 ou numéro de transaction"
                  value={paiementForm.reference_paiement}
                  onChange={(e) => setPaiementForm({ ...paiementForm, reference_paiement: e.target.value })}
                  className="h-9 rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="notes" className="text-sm text-gray-700">Notes</Label>
                <textarea
                  id="notes"
                  rows={3}
                  placeholder="Observations éventuelles..."
                  value={paiementForm.notes}
                  onChange={(e) => setPaiementForm({ ...paiementForm, notes: e.target.value })}
                  className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#246990] focus:outline-none focus:ring-2 focus:ring-[#246990]/15"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="px-5 py-4 border-t bg-gray-50 sm:justify-end gap-2">
            <Button 
              variant="outline"
              className={BOUTON_PIED_CLASSES}
              onClick={() => setShowPaymentModal(false)}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleSavePayment}
              disabled={ecritureEnCours}
              className={`${BOUTON_PIED_CLASSES} bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:opacity-50`}
            >
              {ecritureEnCours
                ? <Loader2 size={16} className="animate-spin" />
                : <CheckCircle size={16} />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmation suppression document */}
      <ConfirmDialog
        isOpen={showDeleteDocModal}
        isLoading={ecritureEnCours}
        onClose={() => {
          setEcritureEnCours(false);
          setShowDeleteDocModal(false);
          setDocumentToDelete(null);
        }}
        onConfirm={handleConfirmDeleteDocument}
        type="error"
        title="Supprimer cette pièce ?"
        description={`« ${documentToDelete?.nom_fichier || 'Ce document'} » sera définitivement retiré du dossier. Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
      />

      {/* Toast */}
      {/* Notification conforme : ToastPortal + bascule LongToast. */}
      <PorteurNotification notification={notification} onFermer={masquer} />
    </div>
  );
};

export default CandidatsLibresSection;